import React, { useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X, Heart, Mail, MapPin, Phone, Instagram, Facebook, Twitter, ShoppingBag } from "lucide-react";
import { toast } from "sonner";
import { useCart } from "@/contexts/CartContext";
import api from "@/lib/api";

const NAV = [
  { to: "/", label: "Home", testid: "nav-home" },
  { to: "/about", label: "About", testid: "nav-about" },
  { to: "/programs", label: "Programs", testid: "nav-programs" },
  { to: "/events", label: "Events", testid: "nav-events" },
  { to: "/gallery", label: "Gallery", testid: "nav-gallery" },
  { to: "/blog", label: "News", testid: "nav-blog" },
  { to: "/shop", label: "Shop", testid: "nav-shop" },
  { to: "/sponsorship", label: "Sponsors", testid: "nav-sponsorship" },
  { to: "/contact", label: "Contact", testid: "nav-contact" },
];

export default function Layout({ children }) {
  const [open, setOpen] = useState(false);
  const { pathname } = useLocation();
  const { count } = useCart();
  const onAdmin = pathname.startsWith("/admin");
  if (onAdmin) return <>{children}</>;

  return (
    <div className="min-h-screen flex flex-col bg-[var(--bcol-bg)]">
      <header className="sticky top-0 z-50 bg-white/95 backdrop-blur border-b border-[var(--bcol-border)]">
        <div className="max-w-7xl mx-auto px-5 lg:px-8 flex items-center justify-between h-20">
          <Link to="/" className="flex items-center gap-3" data-testid="nav-logo">
            <img src="https://customer-assets.emergentagent.com/job_community-voice-71/artifacts/l7wwvehd_ChatGPT%20Image%20May%2028%2C%202026%2C%2002_42_51%20PM.png"
              alt="BCOL — Basse Côte Ohio Lagni" className="h-14 w-14 object-contain" />
            <div className="leading-tight hidden sm:block">
              <div className="font-display text-[var(--bcol-navy)] text-lg font-semibold">BCOL</div>
              <div className="text-[10px] tracking-[0.2em] text-[var(--bcol-muted)] uppercase">Basse Côte Ohio Lagni</div>
            </div>
          </Link>
          <nav className="hidden lg:flex items-center gap-7">
            {NAV.map((n) => (
              <NavLink key={n.to} to={n.to} end={n.to === "/"} data-testid={n.testid}
                className={({ isActive }) =>
                  `text-sm font-medium tracking-wide transition-colors ${
                    isActive ? "text-[var(--bcol-navy)]" : "text-[var(--bcol-muted)] hover:text-[var(--bcol-navy)]"
                  }`
                }>
                {n.label}
              </NavLink>
            ))}
          </nav>
          <div className="hidden lg:flex items-center gap-3">
            <Link to="/cart" className="relative p-2 text-[var(--bcol-navy)] hover:text-[var(--bcol-gold)] transition" data-testid="nav-cart">
              <ShoppingBag className="w-5 h-5" />
              {count > 0 && (
                <span className="absolute -top-1 -right-1 bg-[var(--bcol-gold)] text-[var(--bcol-navy)] text-[10px] font-bold rounded-full w-5 h-5 flex items-center justify-center" data-testid="nav-cart-count">{count}</span>
              )}
            </Link>
            <Link to="/volunteer" className="btn-outline" data-testid="nav-volunteer-btn">Volunteer</Link>
            <Link to="/donate" className="btn-gold" data-testid="nav-donate-btn"><Heart className="w-4 h-4" /> Donate</Link>
          </div>
          <button onClick={() => setOpen(!open)} className="lg:hidden p-2" data-testid="nav-menu-toggle">
            {open ? <X /> : <Menu />}
          </button>
        </div>
        {open && (
          <div className="lg:hidden border-t border-[var(--bcol-border)] bg-white">
            <div className="px-5 py-4 flex flex-col gap-3">
              {NAV.map((n) => (
                <NavLink key={n.to} to={n.to} onClick={() => setOpen(false)}
                  className="py-2 text-[var(--bcol-navy)] font-medium" data-testid={`m-${n.testid}`}>
                  {n.label}
                </NavLink>
              ))}
              <Link to="/volunteer" onClick={() => setOpen(false)} className="btn-outline mt-2 justify-center">Volunteer</Link>
              <Link to="/donate" onClick={() => setOpen(false)} className="btn-gold justify-center">Donate</Link>
            </div>
          </div>
        )}
      </header>

      <main className="flex-1">{children}</main>

      <Footer />
    </div>
  );
}

function Footer() {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const subscribe = async (e) => {
    e.preventDefault();
    if (!email) return;
    setLoading(true);
    try {
      await api.post("/newsletter", { email });
      toast.success("Subscribed! Check your inbox.");
      setEmail("");
    } catch (err) {
      toast.error("Something went wrong");
    } finally { setLoading(false); }
  };

  return (
    <footer className="bg-[var(--bcol-navy)] text-white">
      <div className="kente-line mx-auto mt-0" style={{ height: 4, width: "100%", borderRadius: 0 }} />
      <div className="max-w-7xl mx-auto px-5 lg:px-8 py-16 grid lg:grid-cols-4 gap-12">
        <div>
          <div className="bg-white inline-block rounded-full p-2 mb-4">
            <img src="https://customer-assets.emergentagent.com/job_community-voice-71/artifacts/l7wwvehd_ChatGPT%20Image%20May%2028%2C%202026%2C%2002_42_51%20PM.png"
              alt="BCOL" className="h-20 w-20 object-contain" />
          </div>
          <p className="text-sm text-white/70 leading-relaxed">
            Basse Côte Ohio Lagni — empowering community, celebrating culture, inspiring excellence within the African diaspora.
          </p>
          <div className="flex gap-3 mt-5">
            <a href="#" className="p-2 rounded-full border border-white/15 hover:border-[var(--bcol-gold)] transition" data-testid="social-facebook"><Facebook className="w-4 h-4" /></a>
            <a href="#" className="p-2 rounded-full border border-white/15 hover:border-[var(--bcol-gold)] transition" data-testid="social-instagram"><Instagram className="w-4 h-4" /></a>
            <a href="#" className="p-2 rounded-full border border-white/15 hover:border-[var(--bcol-gold)] transition" data-testid="social-twitter"><Twitter className="w-4 h-4" /></a>
          </div>
        </div>
        <div>
          <div className="eyebrow text-[var(--bcol-gold)] mb-4">Explore</div>
          <ul className="space-y-2 text-sm text-white/75">
            <li><Link to="/about" className="hover:text-[var(--bcol-gold)]">About BCOL</Link></li>
            <li><Link to="/programs" className="hover:text-[var(--bcol-gold)]">Programs</Link></li>
            <li><Link to="/membership" className="hover:text-[var(--bcol-gold)]">Membership</Link></li>
            <li><Link to="/nominate" className="hover:text-[var(--bcol-gold)]">Graduate Nomination</Link></li>
            <li><Link to="/reports" className="hover:text-[var(--bcol-gold)]">Annual Reports</Link></li>
            <li><Link to="/resources" className="hover:text-[var(--bcol-gold)]">Community Resources</Link></li>
          </ul>
        </div>
        <div>
          <div className="eyebrow text-[var(--bcol-gold)] mb-4">Connect</div>
          <ul className="space-y-3 text-sm text-white/75">
            <li className="flex gap-2"><MapPin className="w-4 h-4 mt-0.5 text-[var(--bcol-gold)]" /> Columbus, Ohio, USA</li>
            <li className="flex gap-2"><Mail className="w-4 h-4 mt-0.5 text-[var(--bcol-gold)]" /> info@bassecoteohiolagni.org</li>
            <li className="flex gap-2"><Phone className="w-4 h-4 mt-0.5 text-[var(--bcol-gold)]" /> +1 (614) 588-5325</li>
          </ul>
        </div>
        <div>
          <div className="eyebrow text-[var(--bcol-gold)] mb-4">Newsletter</div>
          <p className="text-sm text-white/70 mb-3">Get community stories, events and opportunities — once a month.</p>
          <form onSubmit={subscribe} className="flex gap-2" data-testid="footer-newsletter-form">
            <input type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
              placeholder="you@email.com"
              className="flex-1 bg-white/10 border border-white/15 rounded-full px-4 py-2.5 text-sm placeholder-white/40 focus:outline-none focus:border-[var(--bcol-gold)]"
              data-testid="footer-newsletter-email" />
            <button disabled={loading} className="btn-gold !py-2.5 !px-5" data-testid="footer-newsletter-submit">
              {loading ? "..." : "Join"}
            </button>
          </form>
        </div>
      </div>
      <div className="border-t border-white/10">
        <div className="max-w-7xl mx-auto px-5 lg:px-8 py-5 flex flex-col sm:flex-row items-center justify-between gap-2 text-xs text-white/50">
          <div>© {new Date().getFullYear()} Basse Côte Ohio Lagni. All rights reserved.</div>
          <div>501(c)(3) Nonprofit · EIN 33-3581533</div>
        </div>
      </div>
    </footer>
  );
}
